import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { EinzugsgebietMap } from "@/components/calv/EinzugsgebietMap";
import { ContactForm } from "@/components/calv/ContactForm";
import { trackConversion } from "@/lib/analytics";
import { QualificationCard } from "@/components/calv/QualificationCard";

export const Route = createFileRoute("/kontakt")({
  head: () => {
    const h = pageHead("contact", "de");
    return { meta: h.meta, links: h.links };
  },
  component: KontaktPage,
});

const channels = [
  {
    label: "E-Mail",
    value: "info@calvarius.com",
    href: "mailto:info@calvarius.com",
    detail: "Antwort zeitnah, werktags",
  },
  {
    label: "Telefon",
    value: "+49 9353 9768 000",
    href: "tel:+4993539768000",
    detail: "Mo – Fr · 9 – 17 Uhr",
  },
];

function KontaktPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Sprechen wir"
          headline={<>Kontakt <em>aufnehmen.</em></>}
          lead="Lassen Sie uns darüber sprechen, wie Ihre Online-Performance wirtschaftlich besser gesteuert werden kann."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Kontakt" }]}
        />

        <Section
          number="02"
          kicker="Wann ein Gespräch sinnvoll ist"
          headline={<>Erst <em>einordnen,</em> dann entscheiden.</>}
          caption="Ein erstes Gespräch dient nicht dem Verkauf, sondern der Einordnung."
        >
          <RevealOnScroll>
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
              <QualificationCard
                tone="bright"
                eyebrow="Gemeinsame Einordnung"
                headline="Wir klären gemeinsam"
                items={[
                  "Wo Sie aktuell stehen",
                  "Welche Ziele Sie verfolgen",
                  "Welche Herausforderungen bestehen",
                  "Ob und wie wir sinnvoll unterstützen können",
                ]}
                footer="Nicht jedes Projekt passt zu uns — und nicht für jedes Projekt sind wir der richtige Partner. Genau das finden wir in einem offenen Gespräch heraus."
              />
              <QualificationCard
                tone="muted"
                eyebrow="Selbst-Einordnung"
                headline={<>Ein Gespräch ist sinnvoll, wenn Sie&hellip;</>}
                items={[
                  "Online-Marketing oder E-Commerce aktiv betreiben",
                  "Relevante Budgets einsetzen oder aufbauen möchten",
                  "Wert auf saubere Strukturen und klare Entscheidungen legen",
                  "Klartext und ehrliche Einordnung schätzen",
                ]}
                footerEyebrow="…und weniger geeignet, wenn"
                footer="Sie eine schnelle Umsetzung ohne strategische Einordnung suchen. Dann sind wir vermutlich nicht der richtige Ansprechpartner."
              />
            </div>

            <div className="mx-auto mt-24 max-w-[880px] border-l-4 border-lime pl-8">
              <p className="font-display text-[clamp(24px,3vw,34px)] font-medium leading-[1.3] text-ink">
                Unser Anspruch ist nicht, möglichst viele Anfragen zu generieren. Sondern{" "}
                <em className="italic font-medium text-ink-3">die richtigen Gespräche</em> zu führen.
              </p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="03"
          kicker="Direkter Draht"
          headline={<>Schreiben Sie uns <em>direkt.</em></>}
          caption="Nutzen Sie das Formular, um uns kurz Ihr Anliegen zu schildern. Je konkreter Ihre Angaben, desto besser können wir uns vorbereiten."
        >
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
            <RevealOnScroll>
              <div className="space-y-px overflow-hidden rounded-lg border border-rule bg-rule">
                {channels.map((c) => (
                  <div key={c.label} className="bg-white p-5">
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
                      {c.label}
                    </div>
                    <div className="mt-2 font-display text-lg text-ink">
                      <a
                        href={c.href}
                        onClick={() => {
                          if (c.href.startsWith("tel:")) trackConversion("phone_number_clicked", { location: "kontakt_page" });
                        }}
                        className="hover:text-cyan-deep"
                      >
                        {c.value}
                      </a>
                    </div>
                    <div className="mt-1 text-sm text-ink-3">{c.detail}</div>
                  </div>
                ))}
              </div>

              <p className="mt-8 text-sm leading-[1.7] text-ink-3">
                Das Erstgespräch ist unverbindlich und dient der gegenseitigen Einschätzung. Sie
                erhalten in jedem Fall eine ehrliche Rückmeldung – auch dann, wenn wir keinen
                Mehrwert sehen.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay={120}>
              <ContactForm />
            </RevealOnScroll>
          </div>
        </Section>

        <Section
          number="04"
          variant="tinted"
          kicker="Standort"
          headline={<>Karlstadt am Main — und überall, <em>wo Mandate uns brauchen.</em></>}
        >
          <RevealOnScroll>
            <div className="mx-auto max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>
                Calvarius arbeitet aus Karlstadt am Main, mitten in Mainfranken. Persönliche Treffen in der Region sind wirtschaftlich tragbar, weil Würzburg, Schweinfurt, Aschaffenburg, Frankfurt und Nürnberg in 25 bis 100 Kilometern erreichbar sind. Für Mandate außerhalb der Region arbeiten wir digital — ein Großteil der laufenden Steuerung funktioniert ohnehin remote.
              </p>
              <p>
                Falls Sie ein persönliches Erst-Gespräch in unserer Region bevorzugen: gerne. Falls Video-Call oder Telefonat reicht: ebenso. Wir richten uns nach Ihrer Präferenz, nicht nach unserer.
              </p>
            </div>
            <EinzugsgebietMap locale="de" />
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="de" number="05" />
      </main>
      <Footer />
    </>
  );
}
